import {createReducer} from 'redux-act';
import {AxiosResponse} from 'axios';
import {FULFILLED, PENDING} from 'redux-promise-middleware';

import {STEPS} from '../../constants/constants';
import {
    fetchSliderFaces,
    updateCurrentFace,
    setCurrentFace,
    setActiveFace,
    setTryOnStep,
    setProductInfo,
    setProductId,
    setCurrentStep,
    setIsTouch,
    setPd,
} from '../../actions/collection/collection';
import {CollectionType, TryOnFaceType} from './collectionType';

export const defaultState: CollectionType = {
    faces: [],
    activeFace: 0,
    productInfo: {
        id: '0',
        name: '',
        link: '',
    },
    pd: 62,
    isTouch: false,
    currentStep: STEPS.INITIAL,
    status: PENDING,
};

/**
 * Adapter adds "?exclude=1" get param to user photos in order to remove them from cloudinary
 * more details here libs/cloudinary/cloudinary.ts
 * @param {TryOnFaceType} tryOnFace - one item of tryon faces
 * @returns {TryOnFaceType[]} - adapted faces
 */
const modifyUrls = (tryOnFace: CollectionType['faces'][0]) => ({
    ...tryOnFace,
    model: `${tryOnFace.model}${tryOnFace.is_user_photo ? '?exclude=1' : ''}`,
    url: `${tryOnFace.url}${tryOnFace.is_user_photo ? '?exclude=1' : ''}`,
});

export const collection = createReducer<CollectionType>({}, defaultState)
    .on(
        `${fetchSliderFaces}_${PENDING}`,
        (state: CollectionType): CollectionType => ({
            ...state,
            status: PENDING,
        }),
    )
    .on(
        `${fetchSliderFaces}_${FULFILLED}`,
        (state: CollectionType, payload: AxiosResponse<Array<TryOnFaceType>>): CollectionType => ({
            ...state,
            faces: payload.data.map(modifyUrls),
            currentStep: payload.data.some(
                (face: TryOnFaceType): boolean => face.is_current && face.is_user_photo,
            )
                ? STEPS.RESULT
                : STEPS.INITIAL,
            status: FULFILLED,
        }),
    )
    .on(
        `${updateCurrentFace}_${PENDING}`,
        (state: CollectionType, payload, meta?: {imageId: string}): CollectionType => ({
            ...state,
            faces: state.faces.map(
                (item: TryOnFaceType): TryOnFaceType => ({
                    ...item,
                    is_current: meta ? item.id === Number(meta.imageId) : false,
                }),
            ),
        }),
    )
    .on(
        setCurrentFace,
        (state: CollectionType, payload): CollectionType => ({
            ...state,
            faces: state.faces.map(
                (item: TryOnFaceType): TryOnFaceType => ({
                    ...item,
                    is_current: item.id === Number(payload),
                }),
            ),
        }),
    )
    .on(
        setActiveFace,
        (state: CollectionType, payload): CollectionType => ({
            ...state,
            activeFace: payload,
        }),
    )
    .on(
        setTryOnStep,
        (state: CollectionType, payload): CollectionType => ({
            ...state,
            currentStep: payload.currentStep || defaultState.currentStep,
        }),
    )
    .on(
        setProductInfo,
        (state: CollectionType, payload): CollectionType => ({
            ...state,
            productInfo: {...payload},
        }),
    )
    .on(
        setProductId,
        (state: CollectionType, payload): CollectionType => ({
            ...state,
            productInfo: {...state.productInfo, id: payload},
        }),
    )
    .on(
        setCurrentStep,
        (state: CollectionType, payload): CollectionType => ({
            ...state,
            currentStep: payload.step,
        }),
    )
    .on(
        setIsTouch,
        (state: CollectionType, payload): CollectionType => ({
            ...state,
            isTouch: payload.isTouch,
        }),
    )
    .on(
        setPd,
        (state: CollectionType, payload): CollectionType => ({
            ...state,
            pd: payload.pd,
        }),
    );
